import 'boxicons'
import 'bootstrap'
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(ScrollTrigger);

// Testimonial Carousel Functionality
class TestimonialCarousel {
    private currentSlide: number = 0;
    private slides: NodeListOf<HTMLElement>;
    private dots: NodeListOf<HTMLButtonElement>;

    constructor() {
        this.slides = document.querySelectorAll('.testimonial-slide');
        this.dots = document.querySelectorAll('.pagination-dots .dot');

      if (this.slides.length === 0) return;

        this.initializeEventListeners();
    }

    private initializeEventListeners(): void {
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                this.goToSlide(index);
            });
        });

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                this.previousSlide();
            } else if (e.key === 'ArrowRight') {
                this.nextSlide();
            }
        });
    }

    private goToSlide(slideIndex: number): void {
        // Remove active class from current slide and dot
        this.slides[this.currentSlide]?.classList.remove('active');
        this.dots[this.currentSlide]?.classList.remove('active');

        // Update current slide index
        this.currentSlide = slideIndex;

        // Add active class to new slide and dot
        this.slides[this.currentSlide]?.classList.add('active');
        this.dots[this.currentSlide]?.classList.add('active');
    }

    private nextSlide(): void {
        const nextIndex = (this.currentSlide + 1) % this.slides.length;
        this.goToSlide(nextIndex);
    }

    private previousSlide(): void {
        const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
        this.goToSlide(prevIndex);
    }

}

// Client Logos Infinite Scroll
class ClientLogoScroll {
    private slider: HTMLElement | null;
    private tween: gsap.core.Tween | null = null;

    constructor() {
        this.slider = document.querySelector('.client-logos-slider');
        if (this.slider) {
            this.initializeInfiniteScroll();
        }
    }

    private initializeInfiniteScroll(): void {
        if (!this.slider) return;

        // Clone all logos to create seamless infinite scroll
        const logos = this.slider.querySelectorAll('.client-logo');
        logos.forEach(logo => {
            const clone = logo.cloneNode(true) as HTMLElement;
            if (this.slider) {
                this.slider.appendChild(clone);
            }
        });

        // Calculate total width of original logos (not including clones)
        let totalWidth = 0;
        for (let i = 0; i < logos.length; i++) {
            totalWidth += (logos[i] as HTMLElement).offsetWidth + 64; // 64px for margins (2rem * 2 * 16px)
        }

        // Create infinite scroll animation
        this.tween = gsap.fromTo(this.slider, {
            x: 0
        }, {
            x: -totalWidth,
            duration: 60, // Adjust speed as needed
            ease: "none",
            repeat: -1
        });

        // Pause on hover
        this.slider.addEventListener('mouseenter', () => {
            this.tween?.pause();
        });

        this.slider.addEventListener('mouseleave', () => {
            this.tween?.resume();
        });
    }
}


// GSAP Animations

async function initializeHeroAnim(): Promise<void> {
    // Wait for fonts to load before creating SplitText
    if ('fonts' in document) {
        try {
            await Promise.race([
                document.fonts.ready,
                new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout
            ]);
        } catch (error) {
            console.warn('Font loading failed, proceeding anyway:', error);
        }
    }

    const split = SplitText.create(".hero-container h1", {
        type: "words",
        mask: "words", // adds extra wrapper element around lines with overflow: clip
        wordsClass: "word++" // class name for word elements
    });
    const splitChars1 = SplitText.create(".hero-container h1 .word3", {
        type: "chars"
    });
    const splitChars2 = SplitText.create(".hero-container h1 .word4", {
        type: "chars"
    });
    const splitChars3 = SplitText.create(".hero-container h1 .word6", {
        type: "chars"
    });

    function textRevert() {
        split.revert();
        splitChars1.revert();
        splitChars2.revert();
        splitChars3.revert();
    }

    gsap.timeline({onComplete: textRevert})
    .set(".hero-container", {opacity:1})
    .from(split.words, {yPercent: 100, ease: "power2", duration: 0.6, stagger: 0.15})
    .addLabel("label1", "-=0.4")
    .to(splitChars1.chars, {duration: 0.75, color: "var(--color-neon)", stagger: {each: 0.1, yoyo: true, repeat: 1, repeatDelay: 1}, ease: "power1.inOut"}, "label1")    
    .to(splitChars2.chars, {duration: 0.75, color: "var(--color-neon)", stagger: {each: 0.1, yoyo: true, repeat: 1, repeatDelay: 1}, ease: "power1.inOut"}, "<+0.5")
    .to(splitChars3.chars, {duration: 0.75, color: "var(--color-neon)", stagger: {each: 0.1, yoyo: true, repeat: 1, repeatDelay: 1}, ease: "power1.inOut"}, "<+0.5")
    .from(".hero-container p", {duration: 0.5, y: 20, opacity:0}, "label1")
    .from(".hero-container .btn-anim", {duration: 0.5, y: 20, opacity:0}, "label1+=0.25")
    .call(() => {stickerRollAnim.play();}, undefined, 0.85); // start sticker roll anim as headline is coming in
}


// Sticker animations

gsap.set(".motech-sticker-circle", {scale: 1.05}); // set initial scale as larger for pulse anim

const stickerRollAnim = gsap.timeline({paused: true, onComplete: () => { stickerArrowAnim.delay(1).play(); } });
    stickerRollAnim
    .to(".motech-sticker", {duration: 0.1, opacity: 1})
    .from(".motech-sticker", {duration: 1, rotation: 360, xPercent: 150, ease: "back.out(2)"}, "<")

const stickerArrowAnim = gsap.timeline({paused: true, onComplete: () => { stickerPulseAnim.play(); }});
    stickerArrowAnim
        .to(".motech-sticker-text", {duration: 0.3, ease: "back.in(3)", scale: 0.75, opacity: 0})
        .from(".motech-sticker-arrow, .motech-sticker-circle", {duration: 0.3, opacity: 0}, 0)
        .to(".motech-sticker-carrot", {duration: 0.01, scale: 0.9, opacity: 0})
        .to(".motech-sticker-arrow", {duration: 1, ease: "back.inOut(1.7)", rotation: -435}) // do a complete loop plus extra to look like a play button
        .to(".motech-sticker-arrow", {duration: 0.15, ease: "sine", scale: 1.2, xPercent: -2, yPercent: 2}, "-=0.45")

const stickerPulseAnim = gsap.timeline({repeat: -1, yoyo: true, paused: true});
    stickerPulseAnim
        .to(".motech-sticker-arrow, .motech-sticker-circle", {duration: 0.6, scale: 1, ease: "sine.inOut"})



// Pillar ScrollTrigger Animations
/*const pillarAnimArray = gsap.utils.toArray<HTMLElement>(".services-pillar");

pillarAnimArray.forEach((pillar) => {

    const serviceItem = pillar.querySelector(".services-item");
    const serviceImage = pillar.querySelector(".service-image");
    const serviceInfo = pillar.querySelector(".service-info");

    const pillarTimeline = gsap.timeline({
        scrollTrigger: {
            trigger: pillar,
            start: "top 60%", // start when top of pillar element hits 60% of viewport height
        }
    });

    const mm = gsap.matchMedia();

    mm.add("(min-width: 769px)", () => {
        pillarTimeline
            .from(serviceItem, {duration: 0.3, yPercent: 10, opacity: 0, ease: "power2"})
            .from(serviceImage, {duration: 0.75, scale: 1.1, opacity: 0, ease: "power3"}, "-=0.1")
            .from(serviceInfo, {duration: 0.5, yPercent: 10, opacity: 0, ease: "power2"}, "<+0.25")

            return () => { // optional
                // custom cleanup code here (runs when it STOPS matching)
                pillarTimeline.clear().pause(0);
            };
    });

    mm.add("(max-width: 768px)", () => {
        pillarTimeline
            .from(pillar, {duration: 0.5, y: 50, opacity: 0, ease: "power2"})

            return () => { // optional
                // custom cleanup code here (runs when it STOPS matching)
                pillarTimeline.clear().pause(0);
            };
    });
});*/

// Carrot ScrollTrigger Animation
gsap.from(".carrot-note img", {
    scrollTrigger: {
        trigger: ".carrot-note > div",
        start: "bottom bottom", // start anim when bottom of trigger element hits bottom of viewport
    },
    yPercent: 100,
    ease: "power2",
    rotation: gsap.utils.wrap([25, -25]),
    duration: 0.6,
    stagger: 0.1
});


// Initialize testimonial carousel and animations when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new TestimonialCarousel();
    new ClientLogoScroll();

  initializeHeroAnim();


  const commercialModal = document.getElementById('commercial');
  const video = document.getElementById('commercialVideo') as HTMLVideoElement | null;

  // Only set up video controls if both modal and video exist
  if (commercialModal && video) {
    // Play video when modal opens
    commercialModal.addEventListener('shown.bs.modal', function () {
      video.currentTime = 0;
      video.play();
    });

    // Stop and reset video when modal closes
    commercialModal.addEventListener('hidden.bs.modal', function () {
      video.pause();
      video.currentTime = 0;
    });
  }
});


